import { useEffect, useState } from "react";
import { getHomeworkOverrides, getMyHomework, saveHomework, saveHomeworkTask, type HomeworkSubmission } from "../lib/firebase";
import { HOMEWORK, HOMEWORK_TEXT_MAX, cleanLink, type Homework } from "../lib/homework";

// One module's homework: the task, the hand-in form, and (admin) the task editor.
// Used on the การบ้าน + Quiz page and inside a lesson that hands in homework (lesson.homeworkId).

export function HomeworkCard({ userId, courseId, hw, sent, isAdmin, onSent, onEdited }: {
  userId: string;
  courseId: string;
  hw: Homework;
  sent?: HomeworkSubmission;
  isAdmin: boolean;
  onSent: (s: HomeworkSubmission) => void;
  onEdited: (h: Homework) => void;
}) {
  const [editingTask, setEditingTask] = useState(false);
  if (editingTask) return <HomeworkTaskEditor hw={hw} onCancel={() => setEditingTask(false)} onSaved={(h) => { onEdited(h); setEditingTask(false); }} />;
  const [editing, setEditing] = useState(!sent);
  const [text, setText] = useState(sent?.text ?? "");
  const [link, setLink] = useState(sent?.link ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const clean = { homeworkId: hw.id, courseId, text: text.trim().slice(0, HOMEWORK_TEXT_MAX), link: cleanLink(link) };
    if (link.trim() && !clean.link) return setError("ลิงก์ต้องขึ้นต้นด้วย https://");
    if (!clean.text && !clean.link) return setError("ใส่ลิงก์งาน หรือเขียนสรุปงานก่อนส่งนะครับ");
    setSaving(true);
    setError(null);
    try {
      await saveHomework(userId, clean);
      onSent({ ...clean, updatedAt: new Date() });
      setEditing(false);
    } catch {
      setError("ส่งไม่สำเร็จ ลองอีกครั้งนะครับ");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="checkpoint hw-card">
      {isAdmin && <button type="button" className="btn-ghost quiz-edit-btn" onClick={() => setEditingTask(true)}>✏️ แก้การบ้าน</button>}
      <h3 className="checkpoint__title">การบ้าน {sent && <span className="hw-sent">ส่งแล้ว ✓</span>}</h3>
      <p className="hw-label">สิ่งที่ต้องส่ง</p>
      <ol>{hw.deliver.map((d) => <li key={d}>{d}</li>)}</ol>
      <p className="hw-label">เช็คว่าผ่าน</p>
      <ul>{hw.pass.map((d) => <li key={d}>{d}</li>)}</ul>
      {hw.time && <p className="hw-time">เวลาที่ควรใช้: {hw.time}</p>}

      {editing ? (
        <div className="hw-form">
          <label>
            ลิงก์งาน (Google Doc, Drive, เว็บ, โพสต์)
            <input type="url" value={link} placeholder="https://" onChange={(e) => setLink(e.target.value)} />
          </label>
          <label>
            สรุปงาน / คำตอบ
            <textarea rows={4} maxLength={HOMEWORK_TEXT_MAX} value={text} onChange={(e) => setText(e.target.value)} />
          </label>
          {error && <p className="error">{error}</p>}
          <div className="checkpoint__actions">
            <button type="button" onClick={submit} disabled={saving}>{saving ? "กำลังส่ง…" : "ส่งการบ้าน"}</button>
            {sent && <button type="button" className="btn-ghost" onClick={() => setEditing(false)}>ยกเลิก</button>}
          </div>
        </div>
      ) : (
        <div className="hw-done">
          {sent?.link && <a href={sent.link} target="_blank" rel="noreferrer">{sent.link}</a>}
          {sent?.text && <p>{sent.text}</p>}
          <button type="button" className="btn-ghost" onClick={() => setEditing(true)}>แก้ไขการบ้าน</button>
        </div>
      )}
    </section>
  );
}

/** Admin: edit one module's homework. One line = one item. Saves to homeworkTasks/{id}. */
function HomeworkTaskEditor({ hw, onSaved, onCancel }: { hw: Homework; onSaved: (h: Homework) => void; onCancel: () => void }) {
  const [deliver, setDeliver] = useState(hw.deliver.join("\n"));
  const [pass, setPass] = useState(hw.pass.join("\n"));
  const [time, setTime] = useState(hw.time ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lines = (t: string) => t.split("\n").map((x) => x.trim()).filter(Boolean);

  async function save() {
    const next: Homework = { ...hw, deliver: lines(deliver), pass: lines(pass), time: time.trim() || undefined };
    if (next.deliver.length === 0) return setError("ต้องมีสิ่งที่ต้องส่งอย่างน้อย 1 ข้อ");
    setSaving(true);
    try {
      await saveHomeworkTask(next);
      onSaved(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }

  return (
    <section className="checkpoint quiz-editor">
      <h3 className="checkpoint__title">แก้การบ้าน</h3>
      <label>สิ่งที่ต้องส่ง <small>(บรรทัดละ 1 ข้อ)</small><textarea rows={6} value={deliver} onChange={(e) => setDeliver(e.target.value)} /></label>
      <label>เช็คว่าผ่าน <small>(บรรทัดละ 1 ข้อ)</small><textarea rows={4} value={pass} onChange={(e) => setPass(e.target.value)} /></label>
      <label>เวลาที่ควรใช้<input value={time} onChange={(e) => setTime(e.target.value)} placeholder="เช่น 2-3 ชั่วโมง" /></label>
      {error && <p className="error">{error}</p>}
      <div className="checkpoint__actions">
        <button type="button" onClick={save} disabled={saving}>{saving ? "กำลังบันทึก…" : "บันทึกการบ้าน"}</button>
        <button type="button" className="btn-ghost" onClick={onCancel}>ยกเลิก</button>
      </div>
    </section>
  );
}

/** The hand-in box inside a lesson (lesson.homeworkId), e.g. Module 4's website link (Dew, 2026-10-27). */
export function LessonHomework({ userId, courseId, homeworkId, isAdmin }: { userId: string; courseId: string; homeworkId: string; isAdmin: boolean }) {
  const [hw, setHw] = useState<Homework | undefined>(HOMEWORK.find((h) => h.id === homeworkId));
  const [sent, setSent] = useState<HomeworkSubmission | undefined>();
  const [ready, setReady] = useState(false);
  useEffect(() => {
    Promise.all([getMyHomework(userId), getHomeworkOverrides()])
      .then(([mine, tasks]) => {
        setSent(mine.get(homeworkId));
        if (tasks.has(homeworkId)) setHw(tasks.get(homeworkId));
      })
      .catch(() => {})
      .finally(() => setReady(true));
  }, [userId, homeworkId]);
  if (!hw || !ready) return null;
  return <HomeworkCard key={hw.id} userId={userId} courseId={courseId} hw={hw} sent={sent} isAdmin={isAdmin} onSent={setSent} onEdited={setHw} />;
}
